import React, { useCallback, useEffect, useState } from 'react';
import { Mail, UserPlus, Users, Trash2, Loader2, Crown, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { RoleBadge } from '@/components/collaboration/RoleBadge';
import {
  shareInviteService,
  type ResourceCollaboratorsResponse,
} from '@/services/shareInvites';
import { toast } from 'sonner';

interface ProjectCollaboratorsPanelProps {
  projectId: string;
  canManage: boolean;
  isOwner?: boolean;
  refreshKey?: number;
  onInviteClick: () => void;
  compact?: boolean;
}

function relativeExpiry(iso?: string): string {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const days = Math.ceil((ts - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'expired';
  if (days === 0) return 'expires today';
  if (days === 1) return 'expires in 1 day';
  return `expires in ${days} days`;
}

function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

function PersonRow({
  title,
  subtitle,
  email,
  role,
  statusLabel,
  statusClass,
  trailing,
}: {
  title: string;
  subtitle?: string;
  email?: string;
  role: string;
  statusLabel?: string;
  statusClass?: string;
  trailing?: React.ReactNode;
}) {
  const displayEmail = email || subtitle;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-khiimori-bg-elevated/40 border border-khiimori-border/50">
      <div className="min-w-0 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-primary-500/15 text-primary-300 flex items-center justify-center text-xs font-semibold shrink-0">
          {displayEmail ? initialsFromEmail(displayEmail) : <Crown className="w-4 h-4" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-khiimori-text-primary truncate">{title}</p>
          {subtitle && (
            <p className="text-xs text-khiimori-text-muted truncate">{subtitle}</p>
          )}
          {statusLabel && (
            <p className={`text-xs mt-0.5 ${statusClass ?? 'text-khiimori-text-muted'}`}>
              {statusLabel}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <RoleBadge role={role} />
        {trailing}
      </div>
    </div>
  );
}

export const ProjectCollaboratorsPanel: React.FC<ProjectCollaboratorsPanelProps> = ({
  projectId,
  canManage,
  isOwner = true,
  refreshKey = 0,
  onInviteClick,
  compact = false,
}) => {
  const [data, setData] = useState<ResourceCollaboratorsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCollaborators = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await shareInviteService.getProjectCollaborators(projectId);
      setData(result);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load collaborators');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void fetchCollaborators();
  }, [fetchCollaborators, refreshKey]);

  const handleCancelInvite = async (invitationId: string) => {
    try {
      await shareInviteService.cancelProjectInvitation(projectId, invitationId);
      toast.success('Invitation cancelled');
      void fetchCollaborators();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to cancel invitation');
    }
  };

  const activeCount = data?.members.length ?? 0;
  const pendingCount = data?.pending_invitations.length ?? 0;
  const totalCount = 1 + activeCount + pendingCount;
  const viewerIsOwner = data?.viewer_is_owner ?? isOwner;

  return (
    <div className={`rounded-xl border border-khiimori-border bg-khiimori-bg-elevated/30 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary-400 shrink-0" />
            <h4 className="text-sm font-semibold text-khiimori-text-primary">
              People with access
            </h4>
          </div>
          {!loading && (
            <p className="text-xs text-khiimori-text-muted mt-1 ml-6">
              {totalCount} total · {activeCount} collaborator{activeCount === 1 ? '' : 's'}
              {pendingCount > 0 ? ` · ${pendingCount} pending` : ''}
            </p>
          )}
        </div>
        {canManage && (
          <Button type="button" size="sm" variant="secondary" onClick={onInviteClick} className="gap-1.5 shrink-0">
            <UserPlus className="w-3.5 h-3.5" />
            Invite
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-khiimori-text-muted py-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading collaborators…
        </div>
      ) : error ? (
        <p className="text-sm text-red-400">{error}</p>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-khiimori-text-muted mb-2">
              Owner
            </p>
            {data?.owner && (
              <PersonRow
                title={`${data.owner.name}${viewerIsOwner ? ' (you)' : ''}`}
                subtitle={data.owner.email || 'Project owner'}
                email={data.owner.email}
                role="owner"
              />
            )}
          </div>

          {(data?.members.length ?? 0) > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-khiimori-text-muted mb-2">
                Active collaborators
              </p>
              <div className="space-y-2">
                {data?.members.map((member) => (
                  <PersonRow
                    key={`${member.email}-${member.joined_at}`}
                    title={member.email}
                    subtitle={
                      member.joined_at
                        ? `Joined ${new Date(member.joined_at).toLocaleDateString()}`
                        : 'Active collaborator'
                    }
                    email={member.email}
                    role={member.role}
                    statusLabel="Has access"
                    statusClass="text-emerald-400/90"
                  />
                ))}
              </div>
            </div>
          )}

          {(data?.pending_invitations.length ?? 0) > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-khiimori-text-muted mb-2">
                Pending invitations
              </p>
              <div className="space-y-2">
                {data?.pending_invitations.map((inv) => (
                  <PersonRow
                    key={inv.id ?? inv.email}
                    title={inv.email}
                    subtitle="Waiting to accept invite"
                    email={inv.email}
                    role={inv.role}
                    statusLabel={`Pending · ${relativeExpiry(inv.expires_at)}`}
                    statusClass="text-amber-400/90"
                    trailing={
                      canManage && inv.id ? (
                        <button
                          type="button"
                          onClick={() => void handleCancelInvite(inv.id!)}
                          className="p-1.5 rounded-md text-khiimori-text-muted hover:text-red-400 hover:bg-red-500/10"
                          title="Cancel invitation"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <Clock className="w-3.5 h-3.5 text-amber-400/80" />
                      )
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {!data?.members.length && !data?.pending_invitations.length && viewerIsOwner && (
            <p className="text-sm text-khiimori-text-muted py-1 flex items-start gap-2">
              <Mail className="w-4 h-4 shrink-0 mt-0.5" />
              Only you have access right now. Invite teammates by email to share this project.
            </p>
          )}

          {!data?.members.length && !data?.pending_invitations.length && !viewerIsOwner && (
            <p className="text-sm text-khiimori-text-muted py-1">
              You are viewing a project shared with you by {data?.owner.name ?? 'the owner'}.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectCollaboratorsPanel;
